import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { setStorageLang, getStorageLang } from '../../stores/Language';

// Supported languages and default language
const supportedLanguages = ['en-US', 'tr-TR', 'az-AZ', 'ru-RU'] as const;
const defaultLanguage = 'tr-TR'; // Set a default language here

type LanguageContextType = {
    translations: Record<string, string>;
    setLang: (lang: typeof supportedLanguages[number]) => void;
    lang: typeof supportedLanguages[number];
};

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

type LanguageProviderProps = {
    children: ReactNode;
};

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
    // Ensure initial language falls back to default if undefined or unsupported
    const initialLang = (getStorageLang() as typeof supportedLanguages[number]) || defaultLanguage;

    const [lang, setLang] = useState<typeof supportedLanguages[number]>(initialLang);
    const [translations, setTranslations] = useState<Record<string, string>>({});

    // Load language JSON file and create a translations map
    const loadTranslations = async (language: typeof supportedLanguages[number]) => {
        try {
            const response = await import(`./locales/${language}.json`);
            const translationsMap: Record<string, string> = {};
            response.default.forEach((item: { Header: string, Content: string }) => {
                translationsMap[item.Header] = item.Content;
            });
            setTranslations(translationsMap);
        } catch (error) {
            console.error(`Dil dosyası yüklenemedi: ${language}`, error);
            // Fallback to default language in case of error
            if (language !== defaultLanguage) {
                loadTranslations(defaultLanguage);
            }
        }
    };

    // Load translations on initial load and when `lang` changes
    useEffect(() => {
        loadTranslations(lang);
    }, [lang]);

    // Language change - only change if the new language is valid and different
    const changeLanguage = (newLang: typeof supportedLanguages[number]) => {
        if (newLang !== lang && supportedLanguages.includes(newLang)) {
            setLang(newLang);
            setStorageLang(newLang); // Save to localStorage
            loadTranslations(newLang); // Load new language translations
        }
    };

    return (
        <LanguageContext.Provider value={{ translations, setLang: changeLanguage, lang }}>
            {children}
        </LanguageContext.Provider>
    );
};

// Custom Hook for language usage
export const useLanguage = () => useContext(LanguageContext);
